import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import {
    selectRoutingGuide,
    selectCreateItemInfo,
    selectAvailableProviders,
} from '../../../store/selectors/routing-guide.selectors';
import { AppState } from '../../../../../store/app.state';
import { RoutingGuide } from '../../../store/models/routing-guide.models';
import { PRODUCT_ICONS } from '../../../../../constants/productMappings';
import { createRoutingGuideItem } from '../../../store/actions/routing-guide.actions';
import { AlgoliaLocationsService } from '../../../../shared/services/algolia-locations.service';

@Component({
    selector: 'app-new-routing-guide-item-modal',
    templateUrl: './new-routing-guide-item-modal.component.html',
    styleUrls: ['./new-routing-guide-item-modal.component.scss'],
})
export class NewRoutingGuideItemModalComponent implements OnInit, OnDestroy {
    private fb = inject(FormBuilder);
    private store = inject(Store<AppState>);
    private config  = inject(DynamicDialogConfig);
    public activeModal = inject(DynamicDialogRef);
    private algoliaLocations = inject(AlgoliaLocationsService);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public rowData: any;
    public providers$ = this.store.select(selectAvailableProviders);
    public itemForm: FormGroup;
    public productIcons = PRODUCT_ICONS;
    public validationErrors = [];
    public creating = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public searchLocationResults: any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public error: any;

    private infoSubscription: Subscription;
    private submitSubscription: Subscription;

    ngOnInit() {
        this.itemForm = this.fb.group({
            origin: [null, [Validators.required]],
            destination: [null, [Validators.required]],
            providers: [[], [Validators.required]],
        });

        if (this.config.data.rowData) {
            const { origin, destination, providers } = this.config.data.rowData;
            this.itemForm.get('origin').setValue(origin);
            this.itemForm.get('destination').setValue(destination);
            this.itemForm.get('providers').setValue(providers);
        }
        this.infoSubscription = this.store
            .select(selectCreateItemInfo)
            .subscribe(info => {
                if (this.creating && !info.creating && !info.error) {
                    // Creation has been completed without error
                    this.activeModal.close();
                }

                this.creating = info.creating;
                this.error = info.error;

                if (this.error && this.error.status === 422) {
                    // Validation errors have occured, concat all so we can display them properly
                    this.validationErrors = [].concat(
                        ...Object.keys(this.error.error).map(
                            k => this.error.error[k]
                        )
                    );
                    this.error = null;
                }

                // Re-enable the form when we're not creating
                if (!info.creating && this.itemForm.disabled) {
                    this.itemForm.enable();
                }
            });
    }

    ngOnDestroy(): void {
        if (this.infoSubscription) {
            this.infoSubscription.unsubscribe();
        }

        if (this.submitSubscription) {
            this.submitSubscription.unsubscribe();
        }
    }

    /**
     * Hamdles searching for locations
     * @param event the event emitted by the typeahead
     */
    searchLocation(event: { query: string }) {
        const searchSubscription = this.algoliaLocations
            .getLocationSuggestions(event.query)
            .subscribe(results => {
                this.searchLocationResults = results;
                searchSubscription.unsubscribe();
            });
    }

    submit() {
        this.validationErrors = [];
        for (const control in this.itemForm.controls) {
            // eslint-disable-next-line no-prototype-builtins
            if (this.itemForm.controls.hasOwnProperty(control)) {
                this.itemForm.controls[control].markAsTouched();
            }
        }

        if (this.itemForm.invalid) {
            return;
        }

        this.submitSubscription = this.store
            .select(selectRoutingGuide)
            .subscribe((routingGuide: RoutingGuide | null) => {
                this.itemForm.disable();
                if (routingGuide) {
                    this.store.dispatch(
                        createRoutingGuideItem({
                            routingGuideId: routingGuide.id,
                            routingGuideItem: this.itemForm.value,
                        })
                    );
                }
            });
        return;
    }

    onLocationSelect(
        event: { value: { locode: any; display_name: any; country_code: any } },
        formControlName: any
    ) {
        const { locode, display_name, country_code } = event.value;
        this.itemForm.get(formControlName)?.setValue({
            locode,
            display_name,
            country_code
        });
    }
}
